/**
 * The app's navigation (04 §0, HANDOFF §0.2): sidebar items, the mobile tab bar and the permission each needs
 * (11 §3). Items the member may not open are hidden; the routes check the same rules (router.tsx).
 */
import {
  Briefcase,
  CreditCard,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  Menu,
  Package,
  Plug,
  ScanSearch,
  Settings,
  Siren,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { TFunction } from "i18next";
import { allows, type Entitlements, type PermissionRule } from "@brevalt/app-core";

export interface NavDef {
  id: string;
  label: (t: TFunction) => string;
  icon: LucideIcon;
  to: string;
  permission?: PermissionRule;
  /** Shown only to members without `product.read` (the Billing role, HANDOFF §0.3). */
  billingOnly?: boolean;
}

export const NAV: NavDef[] = [
  { id: "dashboard", label: (t) => t("shell.dashboard"), icon: LayoutDashboard, to: "/", permission: "product.read" },
  { id: "products", label: (t) => t("shell.products"), icon: Package, to: "/products", permission: "product.read" },
  { id: "findings", label: (t) => t("shell.findings"), icon: ScanSearch, to: "/findings", permission: "product.read" },
  { id: "cases", label: (t) => t("shell.cases"), icon: Briefcase, to: "/cases", permission: "case.read" },
  { id: "documents", label: (t) => t("shell.documents"), icon: FileText, to: "/documents", permission: "product.read" },
  {
    id: "advisories",
    label: (t) => t("shell.advisories"),
    icon: Megaphone,
    to: "/advisories",
    permission: "case.read",
  },
  {
    id: "integrations",
    label: (t) => t("shell.api_integrations"),
    icon: Plug,
    to: "/integrations",
    permission: { anyOf: ["integration.manage", "source.push"] },
  },
  // Fire drills (F9): practice in the test workspace; in production the page leads there.
  { id: "drills", label: (t) => t("shell.fire_drills"), icon: Siren, to: "/drills", permission: "case.read" },
  { id: "team", label: (t) => t("shell.team"), icon: Users, to: "/team", permission: "member.manage" },
  {
    id: "billing",
    label: (t) => t("workspace.billing"),
    icon: CreditCard,
    to: "/settings/billing",
    permission: "billing.manage",
    billingOnly: true,
  },
  { id: "settings", label: (t) => t("shell.settings"), icon: Settings, to: "/settings" },
  { id: "help", label: (t) => t("shell.help"), icon: LifeBuoy, to: "/help" },
];

/** Mobile tab bar: Dashboard, Cases, Findings, More (04 §0). */
export const TAB_IDS = ["dashboard", "cases", "findings"] as const;
export const MORE: NavDef = { id: "more", label: (t) => t("shell.more"), icon: Menu, to: "/more" };

/** The nav item a path belongs to (the longest matching prefix). */
export function activeNav(pathname: string): string {
  if (pathname === "/") return "dashboard";
  // The phone's full-page inbox is reached from the top bar; the tab bar marks "More".
  if (pathname === "/notifications") return "more";
  let best = "";
  let bestLength = 0;
  for (const n of [...NAV, MORE]) {
    if (n.to === "/") continue;
    if ((pathname === n.to || pathname.startsWith(`${n.to}/`)) && n.to.length > bestLength) {
      best = n.id;
      bestLength = n.to.length;
    }
  }
  return best;
}

/** A nav item is shown when the member may open it (the Billing item only without `product.read`). */
export function navVisible(n: NavDef, e: Pick<Entitlements, "tenant">): boolean {
  if (!allows(e.tenant, n.permission)) return false;
  if (n.billingOnly && allows(e.tenant, "product.read")) return false;
  return true;
}

/** Where a member lands on `/` without `product.read` (the Billing role, HANDOFF §0.3). */
export function homeFor(e: Pick<Entitlements, "tenant">): string {
  if (allows(e.tenant, "product.read")) return "/";
  return allows(e.tenant, "billing.manage") ? "/settings/billing" : "/settings";
}
